import { categorySearch } from "@bloomreach/discovery-web-sdk";
import { createInfiniteQuery, keepPreviousData } from "@tanstack/svelte-query";
import { emit } from "~/lib/events";
import {
  resolveBloomreachCredentials,
  toSdkConfiguration,
  BLOOMREACH_STALE_TIME_MS,
} from "~/lib/bloomreach/config";
import {
  buildCategorySearchOptions,
  buildBloomreachCacheKey,
} from "~/lib/bloomreach/query-builders";
import type { BloomreachResponse } from "~/lib/bloomreach/types";
import { pushListState, replaceListState } from "~/lib/url-state";
import { createListingState } from "./create-listing-state.svelte";
import { syncListingUrl } from "./sync-listing-url.svelte";
import { trackListingEvents } from "./track-listing-events.svelte";

export interface PlpListingConfig {
  cardSelector: string;
  accountId?: string;
  domainKey?: string;
  collectionKey?: string;
  rows?: number;
}

export function createPlpListing(config: PlpListingConfig) {
  const { accountId, domainKey } = resolveBloomreachCredentials(config);
  const collectionKey =
    config.collectionKey || import.meta.env.VITE_BLOOMREACH_COLLECTION_KEY || "new-collections";
  const rows = config.rows ?? 48;
  const credentialsPresent = Boolean(accountId && domainKey);

  const listing = createListingState({
    cardSelector: config.cardSelector,
    sessionKey: `plp-card-index:${window.location.pathname}`,
    resolvedRows: () => rows,
    onScrollStop: (f, s, start) => replaceListState(f, s, start),
    makeQuery: ({ filters, sort, initialRows }) =>
      createInfiniteQuery(() => ({
        queryKey: [
          "plp-products",
          {
            accountId,
            domainKey,
            collectionKey,
            activeFilters: filters(),
            activeSort: sort(),
            initialRows,
            rows,
          },
        ],
        enabled: credentialsPresent,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
          const options = buildCategorySearchOptions({
            collectionKey,
            currentUrl: window.location.href,
            rows: pageParam === 0 ? initialRows : rows,
            start: pageParam,
            activeFilters: filters(),
            sort: sort(),
          });

          if (pageParam === 0) {
            const pre = window.DUSK?.plpPrefetch;
            if (pre) {
              // Use buildBloomreachCacheKey (stable _br_uid_2) to match the key stored
              // by plp-prefetch.ts — the real cookie may differ between the two call sites
              // if BrTrk writes _br_uid_2 between module-eval and queryFn execution.
              const thisKey = buildBloomreachCacheKey(
                { accountId, domainKey },
                collectionKey,
                options,
              );
              if (pre.key === thisKey) {
                delete window.DUSK!.plpPrefetch;
                try {
                  const data = await pre.promise;
                  if (data?.response?.docs?.[0]?.pid) {
                    return data;
                  }
                } catch {
                  // network error — fall through to the SDK call
                }
              }
            }
          }

          const sdkConfig = toSdkConfiguration({ accountId, domainKey });
          return categorySearch(
            collectionKey,
            sdkConfig,
            options,
          ) as unknown as Promise<BloomreachResponse>;
        },
        getNextPageParam: (lastPage) => {
          const fetched = lastPage.response.start + lastPage.response.docs.length;
          return fetched < lastPage.response.numFound ? fetched : undefined;
        },
        placeholderData: keepPreviousData,
        staleTime: BLOOMREACH_STALE_TIME_MS,
      })),
  });

  syncListingUrl(listing, {
    push: (f, s) => pushListState(f, s),
  });

  trackListingEvents(listing, {
    onPageViewed: (resultCount, filters, sort) =>
      emit("plp:page_viewed", {
        resultCount,
        collectionKey,
        initialFilters: filters,
        initialSort: sort,
      }),
    onFiltersChanged: (f) => emit("plp:filters_changed", { filters: f }),
  });

  return {
    listing,
    async handleLoadMore() {
      const result = await listing.handleLoadMore();
      if (result) emit("plp:load_more", result);
    },
    handleSortChange(v: string) {
      if (listing.handleSortChange(v)) emit("plp:sort_changed", { sort: v });
    },
    handleReset() {
      listing.resetAll();
      emit("plp:all_cleared");
    },
  };
}

export type PlpListingState = ReturnType<typeof createPlpListing>;
